<script setup lang="ts">
import { useUiStore } from '@/stores/ui.store';
import PkIcon from './PkIcon.vue';

const ui = useUiStore();

const ICONS: Record<string, string> = { success: 'check', error: 'alert', warning: 'alert', info: 'info' };
const TONES: Record<string, string> = {
  success: 'text-success',
  error: 'text-danger',
  warning: 'text-warning',
  info: 'text-info',
};
</script>

<template>
  <Teleport to="body">
    <!--
      Pinned to both edges on a phone, where `w-full` next to `right-4` used to
      push the stack 16px off the left of the screen. From `lg` it goes back to
      a fixed-width column in the corner.

      The offset below `lg` clears the bottom navigation bar — its `h-14` plus
      the same 0.75rem gap — so a toast never lands on top of the tabs. On the
      few screens that hide the bar the stack simply sits a little high, which
      is harmless; being unreachable underneath it would not be.
    -->
    <div
      class="pointer-events-none fixed inset-x-3 bottom-[calc(env(safe-area-inset-bottom)+4.25rem)] z-[60] flex flex-col gap-2 sm:inset-x-auto sm:right-4 sm:w-full sm:max-w-sm lg:bottom-4"
    >
      <TransitionGroup
        enter-active-class="transition duration-200 ease-smooth"
        enter-from-class="opacity-0 translate-x-6"
        leave-active-class="transition duration-150 ease-in absolute"
        leave-to-class="opacity-0 translate-x-6"
        move-class="transition duration-200"
      >
        <div
          v-for="toast in ui.toasts"
          :key="toast.id"
          class="pk-panel pointer-events-auto flex items-start gap-3 px-4 py-3 shadow-overlay"
          role="status"
        >
          <PkIcon :name="ICONS[toast.kind]" :size="18" :class="TONES[toast.kind]" class="mt-0.5" />
          <div class="min-w-0 flex-1">
            <p class="text-sm font-medium">{{ toast.title }}</p>
            <p v-if="toast.body" class="mt-0.5 break-words text-xs text-content-muted">{{ toast.body }}</p>
          </div>
          <button
            v-if="toast.action"
            class="pk-btn-outline -mt-0.5 shrink-0 !py-1 text-xs"
            type="button"
            :disabled="toast.running"
            @click="ui.runAction(toast.id)"
          >
            {{ toast.action.label }}
          </button>

          <button class="pk-btn-ghost -mr-1 -mt-1 !px-1.5 !py-1" type="button" @click="ui.dismiss(toast.id)">
            <PkIcon name="x" :size="14" />
          </button>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>
